import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { supabase } from '../../lib/supabase';
import type { PortalClass, Recording } from '../../types/portal';
import {
  ChevronLeft, ChevronRight, ChevronDown, CheckCircle2, Play, Loader2,
  ArrowDownUp, SkipBack, SkipForward,
} from 'lucide-react';
import YouTubeEmbed from '../../components/student/YouTubeEmbed';
import { isNewRecording } from '../../lib/portalHelpers';

// "Zu Ende geschaut" nur, wenn wirklich fast das ganze Video gelaufen ist.
// Ohne den Anteils-Check würde schon eine kurze Werbeeinblendung (eigene,
// kurze duration) die Lektion als gesehen markieren und die Position auf 0
// zurücksetzen — das Video würde beim nächsten Öffnen von vorne starten.
const isFinished = (seconds: number, duration: number) =>
  duration > 0 && duration - seconds < 20 && seconds >= duration * 0.9;

const StudentWatchPage: React.FC = () => {
  const { id, recordingId } = useParams<{ id: string; recordingId: string }>();
  const navigate = useNavigate();
  const { user } = useStudentAuth();

  const [cls, setCls] = useState<PortalClass | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [positions, setPositions] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [ascending, setAscending] = useState(true);
  const [playlistOpen, setPlaylistOpen] = useState(true);
  // Tracks if we've already auto-completed this recording in this session
  const autoCompletedRef = useRef<Set<string>>(new Set());

  const lastSavedRef = useRef<{ recordingId: string; seconds: number } | null>(null);
  // Zuletzt vom Player gemeldete Stelle — Grundlage fürs Sichern beim Pausieren,
  // Lektionswechsel und Verlassen der Seite.
  const liveRef = useRef<{ recordingId: string; seconds: number; duration: number } | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const uid = user?.id;
    const [{ data: classData }, { data: recData }, { data: progressData }, { data: posData }] =
      await Promise.all([
        supabase.from('portal_classes').select('*').eq('id', id).single(),
        supabase
          .from('recordings')
          .select('*')
          .eq('class_id', id)
          .eq('is_visible', true)
          .order('sort_order', { ascending: true })
          .order('recorded_at', { ascending: true })
          .order('created_at', { ascending: true }),
        uid
          ? supabase.from('recording_progress').select('recording_id').eq('student_id', uid)
          : Promise.resolve({ data: [] as { recording_id: string }[], error: null }),
        uid
          ? supabase.from('recording_positions').select('recording_id, position_seconds').eq('student_id', uid)
          : Promise.resolve({ data: [] as { recording_id: string; position_seconds: number }[], error: null }),
      ]);

    if (!classData) {
      navigate('/dashboard');
      return;
    }

    setCls(classData as PortalClass);
    setRecordings((recData ?? []) as Recording[]);
    setCompletedIds(
      new Set((progressData ?? []).map((p: { recording_id: string }) => p.recording_id))
    );
    setPositions(
      Object.fromEntries(
        (posData ?? []).map((p: { recording_id: string; position_seconds: number }) => [
          p.recording_id,
          p.position_seconds,
        ])
      )
    );
    setLoading(false);
  }, [id, navigate, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const current = recordings.find(r => r.id === recordingId) ?? null;
  const currentIndex = recordings.findIndex(r => r.id === recordingId);
  const isDone = current ? completedIds.has(current.id) : false;

  const prevRecording = currentIndex > 0 ? recordings[currentIndex - 1] : null;
  const nextRecording = currentIndex < recordings.length - 1 ? recordings[currentIndex + 1] : null;

  const savePosition = useCallback(
    (recId: string, seconds: number, duration: number, force = false) => {
      if (!user || !recId) return;
      // Zu Ende geschaut → beim nächsten Mal wieder von vorne beginnen.
      const store = isFinished(seconds, duration) ? 0 : Math.max(0, Math.floor(seconds));
      const last = lastSavedRef.current;
      if (last && last.recordingId === recId) {
        if (force ? last.seconds === store : Math.abs(last.seconds - store) < 5) return;
      }
      lastSavedRef.current = { recordingId: recId, seconds: store };
      // Nur beim erzwungenen Speichern (Pause/Seitenwechsel) in den State
      // übernehmen — sonst würde die Seite alle paar Sekunden neu rendern.
      if (force) {
        setPositions(prev => (prev[recId] === store ? prev : { ...prev, [recId]: store }));
      }
      supabase.from('recording_positions').upsert(
        {
          student_id: user.id,
          recording_id: recId,
          position_seconds: store,
          duration_seconds: duration > 0 ? Math.floor(duration) : null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'student_id,recording_id' }
      ).then(({ error }) => {
        if (error && import.meta.env.DEV) console.error('savePosition error:', error);
      });
    },
    [user]
  );

  // Auto-mark as completed when the video reaches the end
  const handleProgress = useCallback(
    (seconds: number, duration: number) => {
      if (!recordingId) return;
      liveRef.current = { recordingId, seconds, duration };
      savePosition(recordingId, seconds, duration);

      if (
        user &&
        isFinished(seconds, duration) &&
        !completedIds.has(recordingId) &&
        !autoCompletedRef.current.has(recordingId)
      ) {
        autoCompletedRef.current.add(recordingId);
        setCompletedIds(prev => new Set([...prev, recordingId]));
        void supabase
          .from('recording_progress')
          .insert({ student_id: user.id, recording_id: recordingId })
          .then(({ error }) => {
            if (error) {
              // If already exists (duplicate), that's fine
              autoCompletedRef.current.delete(recordingId);
            }
          });
      }
    },
    [savePosition, user, recordingId, completedIds]
  );

  // Pause sofort exakt festhalten, statt auf das nächste 5-Sekunden-Intervall
  // zu warten — sonst liegt die gespeicherte Stelle vor der Pausenposition.
  const handlePause = useCallback(
    (seconds: number, duration: number) => {
      if (!recordingId) return;
      liveRef.current = { recordingId, seconds, duration };
      savePosition(recordingId, seconds, duration, true);
    },
    [savePosition, recordingId]
  );

  const flushPosition = useCallback(() => {
    const live = liveRef.current;
    if (live) savePosition(live.recordingId, live.seconds, live.duration, true);
  }, [savePosition]);

  // Lektionswechsel, Tab-Wechsel und Schließen der Seite: letzte bekannte
  // Position sichern, damit später wirklich dort fortgesetzt wird.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushPosition();
    };
    window.addEventListener('pagehide', flushPosition);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', flushPosition);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      flushPosition();
    };
  }, [flushPosition, recordingId]);

  const toggleCompleted = async () => {
    if (!user || !current || toggling) return;
    setToggling(true);
    const done = completedIds.has(current.id);
    const recId = current.id;

    setCompletedIds(prev => {
      const next = new Set(prev);
      if (done) next.delete(recId);
      else next.add(recId);
      return next;
    });

    const { error } = done
      ? await supabase
          .from('recording_progress')
          .delete()
          .eq('recording_id', recId)
          .eq('student_id', user.id)
      : await supabase
          .from('recording_progress')
          .insert({ student_id: user.id, recording_id: recId });

    if (error) {
      // Rollback so the UI stays in sync with the DB.
      setCompletedIds(prev => {
        const next = new Set(prev);
        if (done) next.add(recId);
        else next.delete(recId);
        return next;
      });
      if (import.meta.env.DEV) console.error('toggleCompleted failed:', error);
    } else if (done) {
      autoCompletedRef.current.delete(recId);
    }
    setToggling(false);
  };

  const goTo = (rec: Recording) => {
    navigate(`/dashboard/kurse/${id}/lektion/${rec.id}`);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-5 w-28 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse" />
        <div className="aspect-video bg-gray-200 dark:bg-neutral-800 rounded-[20px] animate-pulse" />
        <div className="h-8 w-2/3 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse" />
      </div>
    );
  }

  if (!cls) return null;

  if (!current) {
    if (recordings.length > 0) {
      navigate(`/dashboard/kurse/${id}/lektion/${recordings[0].id}`, { replace: true });
      return null;
    }
    navigate(`/dashboard/kurse/${id}`, { replace: true });
    return null;
  }

  const total = recordings.length;
  const completedCount = recordings.filter(r => completedIds.has(r.id)).length;

  return (
    <div className="space-y-4">
      {/* Breadcrumb back */}
      <button
        onClick={() => navigate(`/dashboard/kurse/${id}`)}
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 dark:text-neutral-400 hover:text-[#970000] dark:hover:text-red-400 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Zurück zum Kurs
      </button>

      {/* Main layout: player left, playlist right on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Player + details */}
        <div className="lg:col-span-2 space-y-3">
          {/* Title + done button */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-1">
                {cls.title} · Lektion {currentIndex + 1} von {total}
              </p>
              <h1 className="text-lg sm:text-xl font-bold text-[#111827] dark:text-neutral-100 leading-snug">
                {current.title}
              </h1>
            </div>
            <button
              onClick={toggleCompleted}
              disabled={toggling}
              title={isDone ? 'Als nicht gesehen markieren' : 'Als gesehen markieren'}
              className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition-all disabled:opacity-60 ${
                isDone
                  ? 'bg-gray-50 dark:bg-neutral-800 text-gray-600 dark:text-neutral-300 border border-gray-200 dark:border-neutral-800 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-900'
                  : 'bg-[#970000] text-white hover:bg-[#7a0000]'
              }`}
            >
              {toggling ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">{isDone ? 'Gesehen' : 'Als gesehen markieren'}</span>
              <span className="sm:hidden">{isDone ? 'Gesehen' : 'Markieren'}</span>
            </button>
          </div>

          {/* Video player */}
          <div className="rounded-[16px] overflow-hidden border border-gray-200 dark:border-neutral-800 shadow-[0_10px_35px_rgba(0,0,0,0.05)]">
            <YouTubeEmbed
              key={current.id}
              url={current.youtube_url}
              title={current.title}
              autoplay
              startSeconds={positions[current.id] ?? 0}
              onProgress={handleProgress}
              onPause={handlePause}
            />
          </div>

          {/* Prev / Next navigation */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => prevRecording && goTo(prevRecording)}
              disabled={!prevRecording}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-neutral-800 text-sm font-medium text-gray-600 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <SkipBack className="h-4 w-4" />
              <span className="hidden sm:inline">Vorherige</span>
            </button>

            <p className="text-xs text-gray-400 dark:text-neutral-500 font-medium">{completedCount}/{total} gesehen</p>

            <button
              onClick={() => nextRecording && goTo(nextRecording)}
              disabled={!nextRecording}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-neutral-800 text-sm font-medium text-gray-600 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <span className="hidden sm:inline">Nächste</span>
              <SkipForward className="h-4 w-4" />
            </button>
          </div>

          {/* Metadata */}
          {current.recorded_at && (
            <p className="text-xs text-gray-400 dark:text-neutral-500">
              {new Date(current.recorded_at).toLocaleDateString('de-DE')}
            </p>
          )}
          {current.description && (
            <p className="text-sm text-gray-500 dark:text-neutral-400 whitespace-pre-line leading-relaxed">{current.description}</p>
          )}
        </div>

        {/* Playlist — shown on desktop right, on mobile below the player */}
        <div className="bg-white dark:bg-neutral-900 rounded-[20px] border border-gray-200 dark:border-neutral-800 overflow-hidden">
          <button
            onClick={() => setPlaylistOpen(v => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 pt-3 pb-0 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
          >
            {/* When closed: show current lesson; when open: show plain header */}
            {playlistOpen ? (
              <div className="text-left">
                <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-0.5">
                  Playlist · {total} Lektionen
                </p>
                <h2 className="text-sm font-bold text-gray-900 dark:text-neutral-100">Alle Lektionen</h2>
              </div>
            ) : (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-0.5">
                  Gerade läuft · {currentIndex + 1}/{total}
                </p>
                <p className="text-sm font-bold text-gray-900 dark:text-neutral-100 line-clamp-1">{current.title}</p>
              </div>
            )}
            <div className="flex items-center gap-2 shrink-0 pb-3">
              {total > 1 && playlistOpen && (
                <button
                  onClick={e => { e.stopPropagation(); setAscending(v => !v); }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 dark:text-neutral-300 border border-gray-200 dark:border-neutral-800 rounded-full px-2 py-1 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
                  title="Reihenfolge umkehren"
                >
                  <ArrowDownUp className="h-3 w-3" />
                  {ascending ? 'Älteste' : 'Neueste'}
                </button>
              )}
              <ChevronDown
                className={`h-4 w-4 text-gray-400 dark:text-neutral-500 transition-transform duration-200 ${playlistOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </button>
          {/* Progress bar — always visible */}
          <div className="px-4 pb-3 border-b border-gray-200 dark:border-neutral-800">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold text-gray-400 dark:text-neutral-500">Fortschritt</span>
              <span className="text-[10px] font-semibold text-gray-500 dark:text-neutral-400">
                {completedCount}/{total} · <span className="text-gray-900 dark:text-neutral-100 font-bold">{total > 0 ? Math.round((completedCount / total) * 100) : 0}%</span>
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 dark:bg-neutral-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#970000] rounded-full transition-all duration-500"
                style={{ width: `${total > 0 ? (completedCount / total) * 100 : 0}%` }}
              />
            </div>
          </div>
          {playlistOpen && (
            <ul className="divide-y divide-gray-200 dark:divide-neutral-800 max-h-[50vh] lg:max-h-[65vh] overflow-y-auto">
              {(ascending
                ? recordings.map((rec, i) => ({ rec, originalIndex: i }))
                : recordings.map((rec, i) => ({ rec, originalIndex: i })).reverse()
              ).map(({ rec, originalIndex }) => {
                const active = rec.id === current.id;
                const done = completedIds.has(rec.id);
                return (
                  <li key={rec.id}>
                    <button
                      onClick={() => goTo(rec)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        active ? 'bg-gray-50 dark:bg-neutral-800' : 'hover:bg-gray-50 dark:hover:bg-neutral-800'
                      }`}
                    >
                      <span className="shrink-0">
                        {done ? (
                          <CheckCircle2 className="h-5 w-5 text-[#970000] dark:text-red-400" />
                        ) : active ? (
                          <span className="w-5 h-5 rounded-full bg-[#970000] flex items-center justify-center">
                            <Play className="h-2.5 w-2.5 text-white ml-0.5" fill="currentColor" />
                          </span>
                        ) : (
                          <span className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-neutral-600 flex items-center justify-center">
                            <span className="text-[9px] font-bold text-gray-500 dark:text-neutral-400">{originalIndex + 1}</span>
                          </span>
                        )}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span
                          className={`block text-xs font-semibold leading-snug line-clamp-2 ${
                            active ? 'text-[#970000] dark:text-red-400' : done ? 'text-gray-400 dark:text-neutral-500' : 'text-gray-900 dark:text-neutral-100'
                          }`}
                        >
                          {rec.title}
                        </span>
                        {isNewRecording(rec, done) && (
                          <span className="inline-block mt-1 text-[9px] font-bold tracking-wider uppercase bg-[#970000] text-white px-1.5 py-0.5 rounded">
                            Neu
                          </span>
                        )}
                      </span>
                      {active && nextRecording && (
                        <ChevronRight className="h-3.5 w-3.5 text-[#970000] dark:text-red-400 shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentWatchPage;
